import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { 
  Box, 
  Breadcrumbs, 
  Button, 
  Card, 
  CardContent, 
  Chip, 
  CircularProgress, 
  Divider, 
  Grid, 
  Tab, 
  Tabs, 
  Typography 
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon 
} from '@mui/icons-material';

import { Location } from '../../types/location.types';
import { LocationService } from '../../services/location.service';
import { LocationForm } from '../../components/LocationForm/LocationForm';
import PageContainer from '../../../../../components/layout/PageContainer';
import ConfirmationDialog from '../../../../../components/common/ConfirmationDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`location-tabpanel-${index}`}
      aria-labelledby={`location-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `location-tab-${index}`,
    'aria-controls': `location-tabpanel-${index}`,
  };
}

const LocationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  const [location, setLocation] = useState<Location | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  
  // Fetch location details
  useEffect(() => {
    const fetchLocation = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        const data = await LocationService.getLocation(id);
        setLocation(data);
      } catch (err) {
        console.error('Failed to fetch location:', err);
        setError(err instanceof Error ? err.message : 'Failed to load location details');
        enqueueSnackbar('Failed to load location details', { variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchLocation();
  }, [id, enqueueSnackbar, navigate]);
  
  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  
  // Handle edit toggle
  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };
  
  // Handle form submission
  const handleSubmit = async (values: any) => {
    if (!id) return;
    
    try {
      setIsLoading(true);
      const updatedLocation = await LocationService.updateLocation(id, values);
      setLocation(updatedLocation);
      setIsEditing(false);
      enqueueSnackbar('Location updated successfully', { variant: 'success' });
    } catch (error) {
      console.error('Failed to update location:', error);
      enqueueSnackbar('Failed to update location', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle delete click
  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };
  
  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!id) return;
    
    try {
      setIsDeleting(true);
      await LocationService.deleteLocation(id);
      enqueueSnackbar('Location deleted successfully', { variant: 'success' });
      navigate('/settings/locations');
    } catch (error) {
      console.error('Failed to delete location:', error);
      enqueueSnackbar('Failed to delete location', { variant: 'error' });
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    }
  };
  
  // Handle delete cancel
  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
  };

  // Loading state
  if (isLoading) {
    return (
      <PageContainer title="Loading Location...">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }
  
  // Error state
  if (error) {
    return (
      <PageContainer title="Error">
        <Box p={3}>
          <Typography color="error" variant="h6" gutterBottom>
            Error Loading Location
          </Typography>
          <Typography color="textSecondary" paragraph>
            {error}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            Go Back
          </Button>
        </Box>
      </PageContainer>
    );
  }
  
  // No location found
  if (!location) {
    return (
      <PageContainer title="Location not found">
        <Box p={3}>
          <Typography variant="h6" gutterBottom>Location not found</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      </PageContainer>
    );
  }
  
  return (
    <PageContainer title={location.name}>
      <Box mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          component={Link}
          to="/settings/locations"
          sx={{ mb: 2 }}
        >
          Back to Locations
        </Button>
        
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {location.name}
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link to="/settings">Settings</Link>
              <Link to="/settings/locations">Locations</Link>
              <Typography color="text.primary">{location.name}</Typography>
            </Breadcrumbs>
          </Box>
          
          <Box>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleEditToggle}
              disabled={isEditing || isLoading}
              sx={{ mr: 1 }}
            >
              {isEditing ? 'Cancel Edit' : 'Edit'}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
              disabled={isDeleting || isLoading}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Box>
      
      {isEditing ? (
        <Card>
          <CardContent>
            <LocationForm
              initialValues={location}
              onSubmit={handleSubmit}
              onCancel={handleEditToggle}
              isSubmitting={isLoading} facilities={[]} parentLocations={[]}            />
          </CardContent>
        </Card>
      ) : (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="location tabs">
              <Tab label="Overview" {...a11yProps(0)} />
              <Tab label="Details" {...a11yProps(1)} />
              <Tab label="Sub-Locations" {...a11yProps(2)} disabled={!location.childrenCount} />
            </Tabs>
          </Box>
          
          <TabPanel value={activeTab} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Basic Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box mb={2}>
                      <Typography variant="subtitle2" color="textSecondary">Code</Typography>
                      <Typography variant="body1">{location.code}</Typography>
                    </Box>
                    
                    <Box mb={2}>
                      <Typography variant="subtitle2" color="textSecondary">Name</Typography>
                      <Typography variant="body1">{location.name}</Typography>
                    </Box>
                    
                    <Box mb={2}>
                      <Typography variant="subtitle2" color="textSecondary">Type</Typography>
                      <Chip
                        label={location.locationType}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    
                    <Box mb={2}>
                      <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                      <Chip
                        label={location.isActive ? 'Active' : 'Inactive'}
                        color={location.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Additional Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box mb={2}>
                      <Typography variant="subtitle2" color="textSecondary">Facility</Typography>
                      <Typography variant="body1">{location.facility.name || 'N/A'}</Typography>
                    </Box>
                    
                    {location.parentId && (
                      <Box mb={2}>
                        <Typography variant="subtitle2" color="textSecondary">Parent Location</Typography>
                        <Typography variant="body1">ID: {location.parentId}</Typography>
                      </Box>
                    )}
                    
                    {location.capacity && (
                      <Box mb={2}>
                        <Typography variant="subtitle2" color="textSecondary">Capacity</Typography>
                        <Typography variant="body1">{location.capacity} people</Typography>
                      </Box>
                    )}
                    
                    {location.description && (
                      <Box mb={2}>
                        <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                        <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                          {location.description}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
          
          <TabPanel value={activeTab} index={1}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Detailed Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box mb={2}>
                      <Typography variant="subtitle2" color="textSecondary">Location Type</Typography>
                      <Typography variant="body1">{location.locationType}</Typography>
                    </Box>
                    
                    {location.locationSubType && (
                      <Box mb={2}>
                        <Typography variant="subtitle2" color="textSecondary">Sub-Type</Typography>
                        <Typography variant="body1">{location.locationSubType}</Typography>
                      </Box>
                    )}
                    
                    <Box mb={2}>
                      <Typography variant="subtitle2" color="textSecondary">Created At</Typography>
                      <Typography variant="body1">
                        {new Date(location.createdAt).toLocaleString()}
                      </Typography>
                    </Box>
                    
                    <Box mb={2}>
                      <Typography variant="subtitle2" color="textSecondary">Updated At</Typography>
                      <Typography variant="body1">
                        {new Date(location.updatedAt).toLocaleString()}
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    {location.barcode && (
                      <Box mb={2}>
                        <Typography variant="subtitle2" color="textSecondary">Barcode</Typography>
                        <Typography variant="body1" style={{ fontFamily: 'monospace' }}>
                          {location.barcode}
                        </Typography>
                      </Box>
                    )}
                    
                    {location.qrCode && (
                      <Box mb={2}>
                        <Typography variant="subtitle2" color="textSecondary">QR Code</Typography>
                        <Typography variant="body1" style={{ fontFamily: 'monospace' }}>
                          {location.qrCode}
                        </Typography>
                      </Box>
                    )}
                    
                    {location.floorPlanUrl && (
                      <Box mb={2}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          Floor Plan
                        </Typography>
                        <Box
                          component="img"
                          src={location.floorPlanUrl}
                          alt="Floor Plan"
                          sx={{ maxWidth: '100%', maxHeight: 200, border: '1px solid #ddd', borderRadius: 1 }}
                        />
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </TabPanel>
          
          <TabPanel value={activeTab} index={2}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Sub-Locations
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                {location.childrenCount ? (
                  <Typography>
                    This location has {location.childrenCount} sub-locations. 
                    {/* In a real app, you would list the sub-locations here */}
                  </Typography>
                ) : (
                  <Typography>No sub-locations found.</Typography>
                )}
              </CardContent>
            </Card>
          </TabPanel>
        </>
      )}
      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={isDeleteDialogOpen}
        title="Delete Location"
        message={"Are you sure you want to delete this location? This action cannot be undone."}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
        maxWidth="xs"
      />
    </PageContainer>
  );
};

export default LocationDetailPage;
